import React, { Fragment, useEffect, useRef, useState } from 'react';
import { Socket } from 'socket.io-client';

import { Button, Dialog } from '@material-ui/core';

import useSnackBar from 'hooks/useSnackBar';

import { formatMessage } from 'modules/mobiCopilot/services/format';
import { DefaultMessage } from 'modules/mobiCopilot/utils/interfaces';
import {
  MobiCopilotWhiteIcon,
  Z_INDEX_COMPONENT_LEVEL,
} from 'modules/mobiCopilot/utils/constants';
import {
  useGetMessageById,
  useGetMessagesHistory,
  useGetMessageSuggestions,
  useResetChat,
  useSendMessage,
} from 'modules/mobiCopilot/services/queries';

import { ConfirmClean } from '../ConfirmClean';
import { DefaultContent } from '../DefaultContent';

import * as S from './styles';
import ShinyBadge from 'components/atoms/ShinyBadge';
import Wrapper from 'components/Wrapper';

interface Props {
  currentUserName: string;
  dealerId: number;
  socketInstance: Socket;
}

const SOCKET_TRIGGER_NEW_MESSAGE = 'message:getAll';

export function CopilotActions({
  currentUserName,
  dealerId,
  socketInstance,
}: Props) {
  const [allMessages, setAllMessages] = useState<DefaultMessage[]>([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [dialogConfirmCleanIsOpen, setDialogConfirmCleanIsOpen] = useState(
    false
  );
  const [messageSuggestions, setMessageSuggestions] = useState<string[]>([]);
  const [pendingSocketResponse, setPendingSocketResponse] = useState(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLButtonElement | null>(
    null
  );
  const [unreadNotificationsCount, setUnreadNotificationsCount] = useState<
    number
  >(0);

  const buttonRef = useRef<HTMLButtonElement | null>(null);
  const popoverIsOpen = Boolean(popoverAnchor);

  const { handleSnackBarError } = useSnackBar();

  const handleOnCancelClear = () => setDialogConfirmCleanIsOpen(false);

  const handleOnConfirmClear = () => {
    handleOnCancelClear();
    setAllMessages([]);
  };

  const {
    data: dataMessageSuggestions,
    isLoading: loadingMessageSuggestions,
    mutate: getMessageSuggestions,
  } = useGetMessageSuggestions({
    onError: () => {
      return handleSnackBarError('Falha ao buscar sugestões de mensagens!');
    },
  });

  const {
    data: dataMessageById,
    isLoading: loadingGetMessageById,
    mutate: getMessageById,
  } = useGetMessageById({
    onSuccess: () => {
      !popoverIsOpen &&
        setUnreadNotificationsCount(unreadNotificationsCount + 1);
    },
    onError: () => {
      return handleSnackBarError('Falha ao buscar mensagem!');
    },
  });

  const {
    data: dataMessagesHistory,
    isLoading: loadingMessagesHistory,
    mutate: getMessagesHistory,
  } = useGetMessagesHistory({
    onSuccess: () => {
      if (!allMessages?.length) {
        return getMessageSuggestions({ dealerId });
      }
    },
    onError: () => {
      setAllMessages([]);
      setPendingSocketResponse(false);
      return handleSnackBarError('Falha ao buscar histórico de mensagens!');
    },
  });

  const { mutate: resetChat, isLoading: loadingResetChat } = useResetChat({
    onSuccess: () => handleOnConfirmClear(),
    onError: () => {
      handleOnCancelClear();
      setPendingSocketResponse(false);
      return handleSnackBarError('Falha ao tentar limpar as mensagens!');
    },
  });

  const { mutate: sendMessage } = useSendMessage({
    onError: () => {
      setPendingSocketResponse(false);

      setAllMessages(lastMessages => [...lastMessages].slice(0, -1));

      return handleSnackBarError(
        'Falha ao tentar enviar mensagem! Tente novamente.'
      );
    },
  });

  const handleClosePopover = () => setPopoverAnchor(null);

  const handleTogglePopover = () => {
    return !popoverIsOpen
      ? setPopoverAnchor(buttonRef.current)
      : handleClosePopover();
  };

  const handleGeneralClose = () => {
    setDialogIsOpen(false);
    handleClosePopover();
  };

  const handleOnFullScreen = () => {
    setDialogIsOpen(!dialogIsOpen);
    // handleTogglePopover();
  };

  const handleOnSubmit = (message: string) => {
    if (!message?.length) return;

    sendMessage({ dealerId, message });
    setCurrentMessage('');
    setPendingSocketResponse(true);

    return setAllMessages(lastMessages => [
      ...lastMessages,
      {
        id: Date.now(),
        type: 'default',
        content: message,
        sender: 'client',
        timestamp: Date.now(),
      },
    ]);
  };

  const isLoadingActions = loadingResetChat || loadingMessageSuggestions;

  const isLoadingMessages =
    loadingMessagesHistory || loadingGetMessageById || pendingSocketResponse;

  const renderDefaultContent = ({
    isFullScreen,
  }: {
    isFullScreen: boolean;
  }) => (
    <DefaultContent
      currentMessage={currentMessage}
      currentUserName={currentUserName}
      isFullScreen={isFullScreen}
      isLoading={isLoadingActions}
      lastMessages={allMessages ?? []}
      messageSuggestions={messageSuggestions}
      onClear={() => setDialogConfirmCleanIsOpen(true)}
      onClose={handleGeneralClose}
      onFullScreen={handleOnFullScreen}
      onSubmit={handleOnSubmit}
      pendingSocketResponse={isLoadingMessages}
      setCurrentMessage={setCurrentMessage}
    />
  );

  useEffect(() => {
    if (popoverIsOpen) {
      // * Get history on popover is open
      getMessagesHistory({ dealerId });
    }
  }, [popoverIsOpen]);

  useEffect(() => {
    if (!socketInstance) return;

    // * Listens for new interactions from the socket
    socketInstance.on('message', ({ type, data }) => {
      setPendingSocketResponse(false);

      // * ID do evento enviado pelo socket
      if (type === SOCKET_TRIGGER_NEW_MESSAGE) {
        if (data?.message_id) {
          return getMessageById({ dealerId, messageId: data?.message_id });
        }
        return getMessagesHistory({ dealerId });
      }
    });
    return () => socketInstance.off('message');
  }, []);

  useEffect(() => {
    if (!dataMessagesHistory) return;

    const formatted = dataMessagesHistory
      .map(values => formatMessage(values))
      .flat();

    return setAllMessages(formatted);
  }, [dataMessagesHistory]);

  useEffect(() => {
    if (!dataMessageById) return;

    return setAllMessages(lastMessages => {
      const newMessages = formatMessage(dataMessageById);
      const newMessageIds = newMessages.map(message => message.id);

      return [
        ...lastMessages.filter(message => !newMessageIds.includes(message.id)),
        ...newMessages,
      ];
    });
  }, [dataMessageById]);

  useEffect(() => {
    if (!dataMessageSuggestions) return;

    return setMessageSuggestions([...dataMessageSuggestions]);
  }, [dataMessageSuggestions]);

  useEffect(() => {
    popoverIsOpen && setUnreadNotificationsCount(0);
  }, [popoverIsOpen]);

  return (
    <Fragment>
      <S.Container>
        <ShinyBadge title={unreadNotificationsCount ? '' : 'Novo'}>
          <Button
            className="custom-button"
            onClick={handleTogglePopover}
            ref={buttonRef}
            variant="contained"
          >
            <MobiCopilotWhiteIcon />
          </Button>
        </ShinyBadge>
        {!!unreadNotificationsCount && (
          <S.CircleNotificationPending
            height={'18px'}
            width={'18px'}
            ml={'13px'}
            borderRadius={'50%'}
            style={{ right: 0, top: 0 }}
          >
            <Wrapper fontSize={10}>{unreadNotificationsCount}</Wrapper>
          </S.CircleNotificationPending>
        )}
      </S.Container>
      <S.Popover
        anchorEl={popoverAnchor}
        open={popoverIsOpen}
        onClose={handleGeneralClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <S.PopoverContent>
          {renderDefaultContent({ isFullScreen: false })}
        </S.PopoverContent>
      </S.Popover>
      <Dialog
        fullScreen
        open={dialogIsOpen}
        style={{ zIndex: Z_INDEX_COMPONENT_LEVEL }}
        onClose={() => {
          if (!isLoadingActions) return;
          setDialogIsOpen(false);
        }}
      >
        {renderDefaultContent({ isFullScreen: true })}
      </Dialog>
      <Dialog
        onClose={() => handleOnCancelClear()}
        open={dialogConfirmCleanIsOpen}
        style={{ zIndex: Z_INDEX_COMPONENT_LEVEL }}
      >
        <ConfirmClean
          isLoading={isLoadingActions}
          onCancel={() => handleOnCancelClear()}
          onConfirm={() => resetChat({ dealerId })}
        />
      </Dialog>
    </Fragment>
  );
}
